import { writable, derived } from 'svelte/store';
import { supabase } from '../lib/supabase';
import { user } from './auth';
import { updateNotificationSchedule } from '../lib/notifications';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type ProficiencyLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export interface UserSettings {
  id?: string;
  user_id?: string;
  theme: 'light' | 'dark';
  notifications_enabled: boolean;
  daily_reminder_time?: string | null;
  auto_play_audio: boolean;
  show_phonetic: boolean;
  show_ipa_phonetic: boolean;
  cards_per_session: number;
  pro_status: boolean;
  pro_expires_at?: string | null;
  onboarding_completed: boolean;
  preferred_language: 'cs' | 'ar' | 'en';
  imported_levels?: string[] | null;
  elevenlabs_voice: 'brian' | 'alice';
  proficiency_level: ProficiencyLevel;
  created_at?: string | null;
  updated_at?: string | null;
}

const defaultSettings: UserSettings = {
  theme: 'light',
  notifications_enabled: true,
  auto_play_audio: true,
  show_phonetic: true,
  show_ipa_phonetic: false,
  cards_per_session: 20,
  pro_status: false,
  onboarding_completed: false,
  preferred_language: 'cs',
  imported_levels: [],
  elevenlabs_voice: 'brian',
  proficiency_level: 'A1',
};

// Create writable stores
export const userSettings = writable<UserSettings>(defaultSettings);
export const isLoadingSettings = writable(false);
export const settingsError = writable<string | null>(null);

// Derived store for theme
export const theme = derived(userSettings, ($settings) => $settings.theme);

// Theme mobile configuration
const THEME_MOBILE_CONFIG = {
  light: {
    statusBarStyle: Style.Dark,
    statusBarColor: '#edf1f7', // Computed from light.css --background: 218 40% 97%
  },
  dark: {
    statusBarStyle: Style.Light,
    statusBarColor: '#111418', // Computed from dark.css --background: 222 47% 8%
  },
} as const;

// Settings actions
export const settingsActions = {
  // Load user settings from database
  async loadSettings() {
    isLoadingSettings.set(true);
    settingsError.set(null);

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        userSettings.set(defaultSettings);
        isLoadingSettings.set(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();

      if (error) {
        // If no settings exist, create default settings
        if (error.code === 'PGRST116') {
          await settingsActions.createDefaultSettings(currentUser.id);
        } else {
          throw error;
        }
      } else if (data) {
        // Type guard for theme
        const theme = data.theme === 'dark' ? 'dark' : 'light';
        userSettings.set({ ...data, theme });
        // Apply theme to document
        settingsActions.applyTheme(theme);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      settingsError.set(error instanceof Error ? error.message : 'Failed to load settings');
      userSettings.set(defaultSettings);
      settingsActions.applyTheme(defaultSettings.theme);
    } finally {
      isLoadingSettings.set(false);
    }
  },
  // Create default settings for new user
  async createDefaultSettings(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .insert({
          user_id: userId,
          ...defaultSettings,
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        const theme = data.theme === 'dark' ? 'dark' : 'light';
        userSettings.set({ ...data, theme });
        settingsActions.applyTheme(theme);
      }
    } catch (error) {
      console.error('Error creating default settings:', error);
      throw error;
    }
  },

  // Update settings in database
  async updateSettings(updates: Partial<UserSettings>) {
    isLoadingSettings.set(true);
    settingsError.set(null);

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        throw new Error('No user logged in');
      }

      const { error } = await supabase
        .from('user_settings')
        .update(updates)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      // Fetch updated data separately
      const { data, error: fetchError } = await supabase
        .from('user_settings')
        .select()
        .eq('user_id', currentUser.id)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        const theme = data.theme === 'dark' ? 'dark' : 'light';
        userSettings.set({ ...data, theme });
        // Apply theme if it was updated
        if (updates.theme) {
          settingsActions.applyTheme(updates.theme);
        }
        // Update notification schedule if notification settings changed
        if (
          updates.notifications_enabled !== undefined ||
          updates.daily_reminder_time !== undefined
        ) {
          await updateNotificationSchedule();
        }
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      settingsError.set(error instanceof Error ? error.message : 'Failed to update settings');
    } finally {
      isLoadingSettings.set(false);
    }
  },

  // Toggle theme between light and dark
  async toggleTheme() {
    const currentSettings = await new Promise<UserSettings>((resolve) => {
      const unsubscribe = userSettings.subscribe((value) => {
        resolve(value);
      });
      unsubscribe();
    });

    const newTheme = currentSettings.theme === 'light' ? 'dark' : 'light';
    await settingsActions.updateSettings({ theme: newTheme });
  },

  // Apply theme to document and mobile StatusBar
  async applyTheme(theme: 'light' | 'dark') {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
    }

    // Apply mobile-specific theme
    if (Capacitor.isNativePlatform()) {
      const config = THEME_MOBILE_CONFIG[theme];
      try {
        await StatusBar.setStyle({ style: config.statusBarStyle });
        await StatusBar.setBackgroundColor({ color: config.statusBarColor });
      } catch (error) {
        console.warn('Failed to update StatusBar theme:', error);
      }
    }
  },

  // Toggle a boolean setting
  async toggleSetting(key: keyof UserSettings) {
    const currentSettings = await new Promise<UserSettings>((resolve) => {
      const unsubscribe = userSettings.subscribe((value) => {
        resolve(value);
      });
      unsubscribe();
    });

    if (typeof currentSettings[key] === 'boolean') {
      await settingsActions.updateSettings({
        [key]: !currentSettings[key],
      });
    }
  },

  // Update specific setting value
  async updateSetting<K extends keyof UserSettings>(key: K, value: UserSettings[K]) {
    await settingsActions.updateSettings({ [key]: value });
  },
};

// Initialize settings when user changes
user.subscribe(($user) => {
  if ($user) {
    settingsActions.loadSettings();
  } else {
    userSettings.set(defaultSettings);
    settingsActions.applyTheme('light');
  }
});
